import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Lock, User, ShoppingBag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/components/Layout";
import { isUserAdmin } from "@/lib/api";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("login");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const from = location.state?.from || "/";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res =
      mode === "login"
        ? await login(phone, password)
        : await register(phone, password, name);
    setLoading(false);
    if (res.ok) {
      toast.success(mode === "login" ? "Selamat datang kembali!" : "Akun berhasil dibuat!");
      const loggedUser = res.user;
      if (isUserAdmin(loggedUser)) {
        navigate("/admin", { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAFAFA]">
      <div className="hidden lg:flex flex-col justify-between brand-gradient text-white p-12">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Kalabiru" className="w-12 h-12 rounded-xl object-cover" />
          <div>
            <div className="font-extrabold text-xl">Kalabiru</div>
            <div className="text-xs text-white/70">Preloved Marketplace</div>
          </div>
        </div>
        <div>
          <h1 className="text-4xl font-extrabold leading-tight">Jual beli preloved<br />aman, cepat & terpercaya.</h1>
          <p className="mt-4 text-white/80 max-w-md">Temukan barang bekas berkualitas atau ubah lemarimu jadi cuan. Transaksi dilindungi Kalabiru Safe Pay.</p>
        </div>
        <div className="flex items-center gap-2 text-white/70 text-sm">
          <ShoppingBag className="w-4 h-4" /> Ribuan barang preloved menanti kamu
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src={LOGO_URL} alt="Kalabiru" className="w-10 h-10 rounded-xl object-cover" />
            <div className="font-extrabold text-[#0B4DA2] text-lg">Kalabiru</div>
          </div>

          <h2 className="text-2xl font-extrabold text-gray-900">{mode === "login" ? "Masuk" : "Daftar"}</h2>
          <p className="text-sm text-gray-500 mt-1">Gunakan nomor HP untuk melanjutkan.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            {mode === "register" && (
              <div className="relative">
                <User className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Nama lengkap"
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#0B4DA2] outline-none text-sm"
                />
              </div>
            )}
            <div className="relative">
              <Phone className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="phone-login-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="0812xxxxxxxx"
                required
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#0B4DA2] outline-none text-sm"
              />
            </div>
            <div className="relative">
              <Lock className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                required
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#0B4DA2] outline-none text-sm"
              />
            </div>
            <button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full py-3 rounded-xl bg-[#0B4DA2] hover:bg-[#083B7C] text-white font-semibold transition disabled:opacity-60"
            >
              {loading ? "Memproses..." : mode === "login" ? "Masuk" : "Daftar"}
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-6">
            {mode === "login" ? "Belum punya akun?" : "Sudah punya akun?"}{" "}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-[#0B4DA2] font-semibold"
            >
              {mode === "login" ? "Daftar" : "Masuk"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
